// Shared XLSX export — the sibling of src/utils/exportCsv.js. Build once; every table reuses it.
//
//   exportToXlsx(filename, columns, rows, options)
//     columns: [{ key, label, type }]  — SAME shape as exportToCsv, plus an optional `type`
//     rows:    array of objects keyed by column.key (null/undefined -> empty cell)
//     options: { sheetName, totals, footnotes, estimatedColumns, titleRows, filterRows, trailingRows }
//
// ⛔ THE ARGUMENT SHAPE MIRRORS exportToCsv ON PURPOSE: a caller swaps `exportToCsv` for
// `exportToXlsx` with no other change. Everything XLSX adds (types, totals, footnotes,
// multiple sheets) is optional. Download is the same mechanism — Blob + a temporary
// <a download> — so both behave identically from the caller's side.
//
// MULTI-SHEET: pass an array of sheet descriptors as the SECOND argument instead of columns:
//   exportToXlsx('book.xlsx', [{ name, columns, rows, totals, footnotes }, ...])
// A descriptor may instead carry a finished worksheet, { name, ws } — a layout this builder can't express (the Tax
// Savings Appeal Summary's live formulas, title in column D — src/reports/taxSavingsAppealSummary.js).
// A sheet descriptor always carries `rows`; a column object never does, which is the
// discriminator. exportSheetsToXlsx is also exported directly for callers who prefer explicit.
//
// ⛔ COMMUNITY-BUILD LIMITS, all verified by writing a file and unzipping it — do not re-litigate
// these from documentation, the docs describe the paid build:
//   • cell.s (bold/fill/border)  DROPPED. A totals row is marked by its label and a blank spacer
//     row above it, not by weight.
//   • ws['!freeze'] / ws['!panes']  DROPPED. <sheetView> writes bare, no frozen header.
//   • ws['!autofilter']  WORKS, and an explicit ref narrower than !ref is respected — which is why
//     the filter below is scoped to header+data and deliberately EXCLUDES the totals and footnote
//     rows. Left at the full range, a user could filter the TOTAL row away or sort it into the data.
//   • cell.z (number formats) and ws['!cols'] (widths)  WORK.

import * as XLSX from 'xlsx';

// Number formats by column type. 'value' = whole dollars, 'money' = two decimals — the same
// split the report definitions use. A literal suffix in the format is how an estimate keeps
// its asterisk WITHOUT turning the cell into text (see cellFormat).
const NUM_FMT = {
  money: '"$"#,##0.00',
  value: '"$"#,##0',
  int: '#,##0',
  year: '0',           // a year is a number but must never carry a thousands separator
  // ⛔ A PLAIN NUMBER IN PERCENT UNITS, format copied from the analyst's AutoNation workbook.
  // NOT a true percentage cell (value 0.01178467 + a % format), which was the first attempt: it is
  // arithmetically safer, because =taxable*rate then just works, but her file stores 1.058001 as a
  // float and keeps a second column holding =+K8/100 to multiply by. She does the /100 herself, in
  // a visible cell. These workbooks sit beside hers on a desk, so matching her wins over being
  // cleverer than her.
  rate: '#,##0.000000',
  // A percentage CHANGE. The value is in percent units (41.18), so the % is a literal suffix in
  // the format, NOT Excel's percent type — the same unit convention as `rate`, deliberately.
  percent: '#,##0.00"%"',
};
const DATE_FMT = 'mm/dd/yyyy';
const NUMERIC_TYPES = new Set(['money', 'value', 'int', 'year', 'rate', 'percent']);

// ⛔ ROUND THE CELL VALUE, not just its display format. A format hides 719254.0399999999;
// the stored value still carries it, and anything reading the cell downstream inherits the noise.
const OUTPUT_DP = { money: 2, value: 2, rate: 6, percent: 2, int: 0, year: 0 };
const roundOut = (n, type) => (OUTPUT_DP[type] == null ? n
  : Math.round((n + Number.EPSILON) * 10 ** OUTPUT_DP[type]) / 10 ** OUTPUT_DP[type]);

const MIN_WIDTH = 8;
const MAX_WIDTH = 46;   // cap: one long legal description must not push every other column off-screen

// A display value may be a bare primitive or { value, estimated, recovered, effective } — the report layer's
// shape for "this figure is an estimate", "this figure rests on a rate we reconstructed" and "this rate is the
// bill's ad valorem ÷ taxable". Unwrap to the raw value and keep the flags alongside.
// ⛔ `*`, `†` and `‡` mean different things (see RECOVERED_FOOTNOTE in src/reports/format.js) and a
// client's workbook has to carry the distinction the screen shows, or the caveat is lost in the
// one artifact that leaves the building.
function unwrap(raw) {
  if (raw && typeof raw === 'object' && !(raw instanceof Date) && 'value' in raw) {
    return { value: raw.value, estimated: !!raw.estimated, recovered: !!raw.recovered, effective: !!raw.effective };
  }
  return { value: raw, estimated: false, recovered: false, effective: false };
}

const marksOf = (raw) => { const u = unwrap(raw); return `${u.estimated ? '*' : ''}${u.recovered ? '†' : ''}${u.effective ? '‡' : ''}`; };

// A cell's explanation ({ value, note } — e.g. why a percent reads 'n/m'), written as a hidden cell COMMENT so it
// shows on hover in Excel, as the title does on screen. Comments verified in this community build by unzipping
// the written file (xl/comments1.xml + the vmlDrawing that anchors it).
const noteOf = (raw) => (raw && typeof raw === 'object' && !(raw instanceof Date) && 'value' in raw && raw.note) || null;

// '$1,234.56' / '1234.56' / 1234.56 -> 1234.56. Returns null when there is no number in there,
// so an unparseable cell lands empty rather than as a misleading 0.
// ⛔ FULL-STRING MATCH, not parseFloat's prefix scan. parseFloat('3-year average') returns 3, which
// is how a label in a numeric column silently became a figure — caught in the Summary tab, where
// the average row read "3" and the TOTAL label disappeared entirely.
const NUMERIC_STRING = /^-?(\d+(\.\d*)?|\.\d+)$/;
function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v ?? '').replace(/[$\s,]/g, '');
  if (!s || !NUMERIC_STRING.test(s)) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// Dates are LOCAL-time only in this codebase — never new Date('YYYY-MM-DD'), which parses as UTC
// midnight and lands a day early west of Greenwich.
function toLocalDate(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

// The cell value Excel should hold: a real number for numeric types (or Excel cannot sum the
// column), a real Date for dates, a string otherwise.
function cellValue(raw, type) {
  const { value } = unwrap(raw);
  if (type === 'bool') return value === true;        // always explicit TRUE/FALSE, never blank
  if (value == null || value === '') return null;
  if (type === 'date') return toLocalDate(value) ?? String(value);
  if (NUMERIC_TYPES.has(type)) {
    const n = toNumber(value);
    // A label deliberately placed in a numeric column ("TOTAL", "3-year average") writes as TEXT
    // rather than being dropped. Dropping it is how a totals row loses its label.
    return n != null ? roundOut(n, type) : String(value);
  }
  if (value instanceof Date) return value;
  return String(value);
}

// The number format for one cell. A marked figure keeps its mark as a format suffix, so the cell
// stays numeric — sortable, summable, pivotable — and still reads "$1,234.56*†" on screen.
function cellFormat(raw, type) {
  const marks = marksOf(raw);
  const base = type === 'date' ? DATE_FMT : NUM_FMT[type];
  if (!base) return marks ? '@' : null;              // text column: nothing to format
  return marks ? `${base}"${marks}"` : base;
}

// What the cell will look like, for width measurement only.
function displayLength(raw, type) {
  const { value } = unwrap(raw);
  if (value == null || value === '') return 0;
  let s;
  if (NUMERIC_TYPES.has(type)) {
    const n = toNumber(value);
    s = n == null ? String(value)
      : type === 'money' ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Math.round(n).toLocaleString();
    if (type === 'money' || type === 'value') s = `$${s}`;
    if (type === 'year') s = String(value);
  } else if (type === 'date') {
    s = 'mm/dd/yyyy';
  } else {
    s = String(value);
  }
  return s.length + marksOf(raw).length;
}

// Build one worksheet. `totals` is a row object in the same shape as a data row; when present it
// is written after a blank spacer row, with the first column falling back to 'TOTAL'.
// `footnotes` are written one per row below everything, in the first column.
// Which columns actually carry an estimate flag anywhere in this data set. Only those get a
// companion column — emitting "Estimated" beside every text column would be noise.
export function estimateCarriers(columns, rows, totals) {
  const carriers = new Set();
  const scan = (row) => { if (row) (columns || []).forEach((c) => { if (unwrap(row[c.key]).estimated) carriers.add(c.key); }); };
  (rows || []).forEach(scan);
  scan(totals);
  return carriers;
}

// A number format cannot be pivoted or filtered on. When `estimatedColumns` is on, each
// estimate-carrying column gains a real boolean column immediately after it so an analyst can
// filter in Excel. Labelled "<Label> Estimated" rather than a bare "Estimated" — several columns
// can carry flags independently, and two columns named "Estimated" would be worse than verbose.
function expandColumns(columns, rows, totals, enabled) {
  const cols = (columns || []).filter(Boolean);
  if (!enabled) return cols;
  const carriers = estimateCarriers(cols, rows, totals);
  if (!carriers.size) return cols;
  const out = [];
  cols.forEach((c) => {
    out.push(c);
    if (carriers.has(c.key)) out.push({ key: `__estimated__${c.key}`, label: `${c.label ?? c.key} Estimated`, type: 'bool', _flagFor: c.key });
  });
  return out;
}

function buildSheet({ columns, rows, totals, footnotes, estimatedColumns, titleRows, filterRows, trailingRows }) {
  const cols = expandColumns(columns, rows, totals, estimatedColumns);
  // A companion column reads its value from the flag on the column it shadows.
  const valueOf = (row, c) => (c._flagFor ? unwrap(row?.[c._flagFor]).estimated : row?.[c.key]);

  // ── TITLE BLOCK ────────────────────────────────────────────────────────────────────────────
  // `titleRows` is an array of strings (or nulls for blank rows) written into column A above the
  // header. Everything below shifts down by its length, INCLUDING the autofilter — a filter still
  // anchored at row 1 would treat the client name as the header row.
  const title = Array.isArray(titleRows) ? titleRows : [];
  const offset = title.length;               // 0 = header on row 1, the original behaviour

  const data = (rows || []).map((row) => {
    const o = {};
    // Keyed by column.key, NOT by label — two columns may legitimately share a label, and
    // json_to_sheet would collapse them into one.
    cols.forEach((c) => { o[c.key] = cellValue(valueOf(row, c), c.type); });
    return o;
  });

  const ws = XLSX.utils.aoa_to_sheet([[]]);
  title.forEach((t, i) => {
    if (t == null || t === '') return;
    ws[XLSX.utils.encode_cell({ r: i, c: 0 })] = { t: 's', v: String(t) };
  });
  XLSX.utils.sheet_add_json(ws, data, {
    header: cols.map((c) => c.key), cellDates: true, origin: { r: offset, c: 0 },
  });

  // Replace the machine keys in the header row with the human labels.
  cols.forEach((c, i) => {
    ws[XLSX.utils.encode_cell({ r: offset, c: i })] = { t: 's', v: c.label ?? c.key };
  });

  // Number/date formats, per cell so an estimated figure can differ from its neighbours; and any cell's note.
  (rows || []).forEach((row, ri) => {
    cols.forEach((c, ci) => {
      const cell = ws[XLSX.utils.encode_cell({ r: offset + ri + 1, c: ci })];
      const note = c._flagFor ? null : noteOf(row?.[c.key]);
      if (cell && note) { cell.c = [{ a: 'WigleyHub', t: note }]; cell.c.hidden = true; }
      const z = cellFormat(valueOf(row, c), c.type);
      if (!z) return;
      if (cell && (cell.t === 'n' || cell.t === 'd')) cell.z = z;
    });
  });

  let nextRow = offset + (rows || []).length + 1;

  if (totals) {
    nextRow += 1;                                   // blank spacer row — the only "emphasis" available
    cols.forEach((c, ci) => {
      // A companion flag on the totals row is noise unless the total ITSELF is an estimate —
      // an explicit FALSE under a column of TRUE/FALSE reads as data, not as "not applicable".
      if (c._flagFor && !unwrap(totals[c._flagFor]).estimated) return;
      const raw = valueOf(totals, c);
      const v = ci === 0 && (raw == null || raw === '') ? 'TOTAL' : cellValue(raw, c.type);
      if (v == null || v === '') return;
      const addr = XLSX.utils.encode_cell({ r: nextRow, c: ci });
      ws[addr] = v instanceof Date ? { t: 'd', v }
        : typeof v === 'number' ? { t: 'n', v }
        : typeof v === 'boolean' ? { t: 'b', v }      // a real boolean, not the string "false"
        : { t: 's', v };
      const z = cellFormat(raw, c.type);
      if (z && ws[addr].t !== 's') ws[addr].z = z;
    });
    nextRow += 1;
  }

  // ⛔ ROWS AFTER THE TOTAL. Her Summary reads years, Total, blank, average — the average is a
  // conclusion drawn FROM the total, so it belongs below it. Putting it in `rows` reversed that AND
  // dragged it inside the autofilter, where sorting by any column moved it into the data.
  // Rendered with the data columns and formats, but outside both the filter range and the sum.
  (trailingRows || []).forEach((tr) => {
    nextRow += 1;                                   // blank spacer before each
    cols.forEach((c, ci) => {
      const raw = valueOf(tr, c);
      const v = cellValue(raw, c.type);
      if (v == null || v === '') return;
      const addr = XLSX.utils.encode_cell({ r: nextRow, c: ci });
      ws[addr] = v instanceof Date ? { t: 'd', v }
        : typeof v === 'number' ? { t: 'n', v }
        : typeof v === 'boolean' ? { t: 'b', v }
        : { t: 's', v };
      const z = cellFormat(raw, c.type);
      if (z && ws[addr].t !== 's') ws[addr].z = z;
    });
    nextRow += 1;
  });

  (footnotes || []).filter(Boolean).forEach((note, i) => {
    ws[XLSX.utils.encode_cell({ r: nextRow + 1 + i, c: 0 })] = { t: 's', v: String(note) };
  });
  if ((footnotes || []).length) nextRow += 1 + footnotes.length;

  // Size !ref over the title block, the data, the totals and the footnotes.
  const lastRow = Math.max(nextRow, offset + (rows || []).length, title.length);
  ws['!ref'] = XLSX.utils.encode_range(
    { r: 0, c: 0 },
    { r: Math.max(lastRow, 0), c: Math.max(cols.length - 1, 0) },
  );

  // Autofilter over HEADER + DATA ONLY, anchored at the header row wherever the title block put
  // it. Scoped deliberately short of the spacer/totals/footnote rows: at the full range a user
  // could filter the TOTAL row away, or sort it into the data.
  // `filterRows` lets a sheet end the filter BEFORE its last row. The Summary tab's "3-year
  // average" line sits in the data block but is not data: inside the range, sorting by Taxes moves
  // the average into the middle of the years. Same treatment the TOTAL row already gets by being
  // below the range.
  const filterN = Math.min(
    Number.isFinite(filterRows) ? filterRows : (rows || []).length,
    (rows || []).length,
  );
  if (cols.length && filterN > 0) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({ r: offset, c: 0 }, { r: offset + filterN, c: cols.length - 1 }),
    };
  }

  // Column widths from the widest cell in each column, floored and capped. The title block is
  // NOT measured — a long report title in A2 must not stretch the first data column.
  ws['!cols'] = cols.map((c) => {
    let w = String(c.label ?? c.key).length;
    (rows || []).forEach((row) => { w = Math.max(w, displayLength(valueOf(row, c), c.type)); });
    if (totals) w = Math.max(w, displayLength(valueOf(totals, c), c.type));
    (trailingRows || []).forEach((tr) => { w = Math.max(w, displayLength(valueOf(tr, c), c.type)); });
    return { wch: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w + 2)) };
  });

  return ws;
}

// Excel rejects : \ / ? * [ ] in a sheet name, and truncates past 31 characters.
function safeSheetName(name, fallback) {
  const s = String(name || fallback || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').trim();
  return (s || fallback || 'Sheet').slice(0, 31);
}

function download(wb, filename) {
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = /\.xlsx$/i.test(filename) ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// One workbook, many sheets: [{ name, columns, rows, totals, footnotes }]
export function exportSheetsToXlsx(filename, sheets) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  (sheets || []).forEach((sheet, i) => {
    let name = safeSheetName(sheet?.name, `Sheet${i + 1}`);
    // Excel refuses duplicate sheet names; disambiguate rather than throw.
    let n = 2;
    while (used.has(name.toLowerCase())) name = safeSheetName(`${sheet?.name || `Sheet${i + 1}`} ${n++}`, `Sheet${i + 1}`);
    used.add(name.toLowerCase());
    XLSX.utils.book_append_sheet(wb, sheet?.ws || buildSheet(sheet || {}), name);
  });
  download(wb, filename);
}

export function exportToXlsx(filename, columns, rows, options = {}) {
  // Multi-sheet form: the second argument is an array of sheet descriptors, each carrying `rows`.
  if (Array.isArray(columns) && columns.length && columns[0] && typeof columns[0] === 'object' && 'rows' in columns[0]) {
    return exportSheetsToXlsx(filename, columns);
  }
  return exportSheetsToXlsx(filename, [{
    name: options.sheetName || 'Report',
    columns,
    rows,
    totals: options.totals,
    footnotes: options.footnotes,
    estimatedColumns: options.estimatedColumns,
    titleRows: options.titleRows,
    filterRows: options.filterRows,
    trailingRows: options.trailingRows,
  }]);
}

export default exportToXlsx;
